<template>
  <v-card
    class="elevation-3 fade-in-up bg-dot-pattern"
    light
  >
    <v-card-text>
      <content-section
        id="timeline"
        :title="detailed ? 'My Adventures so far' : 'My Work Experiences'"
      >
        <template slot="actions">
          <div>
            <v-switch
              v-model="detailed"
              :label="detailed ? 'Detailed' : 'Summary'"
            />
          </div>
        </template>

        <v-timeline
          dense
        >
          <v-timeline-item
            v-for="(item, i) in orderedItems"
            :key="i"
            :icon="item.icon || ''"
            :class="{transparent: item.transparent}"
            large
          >
            <template
              v-if="item.iconImage"
              v-slot:icon
            >
              <v-avatar>
                <img
                  :src="publicPath(item.iconImage)"
                >
              </v-avatar>
            </template>
            <template v-slot:opposite />
            <v-layout>
              <v-flex
                v-if="$vuetify.breakpoint.smAndUp"
                md1
                sm2
                align-self-center
              >
                <span class="font-weight-bold red--text text--darken-2">{{ item.year }}</span>
              </v-flex>
              <v-flex
                md11
                sm10
                xs12
              >
                <v-card class="elevation-2 timeline-card">
                  <v-card-title class="pb-0">
                    <div>
                      <p v-if="$vuetify.breakpoint.xsOnly" class="font-weight-bold red--text text--darken-2">
                        {{ item.year }}
                      </p>
                      <h3 class="blue--text text--darken-4">{{ item.title }}</h3>
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-layout wrap>
                      <v-flex
                        :md7="!!item.image"
                        :md12="!item.image"
                        xs12
                      >
                        <div class="mr-1 mt-2 text-body-1 grey--text text--darken-3">
                          <span
                            v-if="item.text"
                            class="pre"
                          >{{ item.text }}</span>
                          <div
                            v-else-if="item.html"
                            v-html="item.html"
                          />
                          </div>
                      </v-flex>
                      <v-flex
                        v-if="item.image"
                        md5
                        xs12
                      >
                        <div
                          class="mt-2"
                        >
                          <v-carousel
                            v-if="Array.isArray(item.image)"
                            :show-arrows="false"
                            :height="325"
                          >
                            <v-carousel-item
                              v-for="(citem,ci) in item.image"
                              :key="ci"
                              :src="publicPath(citem)"
                            />
                          </v-carousel>
                          <v-img
                            v-else
                            :max-height="item.imageHeight ? item.imageHeight : ''"
                            :src="publicPath(item.image)"
                          />
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-timeline-item>
        </v-timeline>
      </content-section>
    </v-card-text>
  </v-card>
</template>

<script>
import ContentSection from '@/views/dark-template/content/Section'
export default {
  name      : 'Timeline',
  components: { ContentSection },
  data      : () => ({
    detailed: false,
    items: [
      // --- PERSONAL HISTORY ---
      {
        detailed   : true,
        transparent: true,
        year       : '1978',
        title      : 'The Beginning',
        html       : 'Born in March 1978. The start of the journey.',
        icon       : 'mdi-cake-variant',
      },
      {
        detailed   : true,
        transparent: true,
        year       : '1996',
        title      : 'Early Tech Enthusiast',
        html       : 'Before "DevOps" existed, I was running BBS systems, modifying hardware, and learning the fundamentals of how systems talk to each other.', 
        image      : 'img/timeline/bulletinboardsystem.png',
        imageHeight: 280,
        icon       : 'mdi-chip',
      },
      
      // --- EARLY MILITARY & CAREER ---
      {
        detailed   : false,
        year       : '1998 - 2013',
        transparent: true, 
        title      : 'Staff Sergeant - U.S. Army',
        html       : `<p>
                      <strong>Leadership Under Pressure.</strong><br>
                      Served 15 years, rising to Staff Sergeant. Deployed as NCOIC of the Trial Defense Service Office, managing legal support for Soldiers in high-stakes environments. 
                      This experience instilled the discipline, crisis management, and "mission-first" mentality that defines my leadership style today.
                      </p>`,
        iconImage  : 'img/timeline/staffsergeant.jpg',
      },
      
      // --- EDUCATION ---
      {
        detailed   : false,
        transparent: true,
        year       : '2016',
        title      : 'B.S. Computer Science - Lamar University',
        html       : `<p>
                      <strong>Bridging the Gap between Management and Code.</strong><br>
                      Graduated with a focus on Game Design and a Minor in Business Management. 
                      Completed a 4-year degree in 3 years while maintaining Dean's List status. 
                      This technical foundation allows me to speak the same language as the engineers I lead.
                      </p>`,
        iconImage  : 'img/timeline/LU.png',
      },

      // --- NEW: RAVE FINANCIAL / MOBILOIL ---
      {
        detailed   : true,
        transparent: true,
        year       : '2016 - 2019',
        title      : 'Systems and Applications Programmer - Rave Financial',
        html       : `<p>
                      <strong>Database Architecture & Automation.</strong><br>
                      Served as Lead Programmer, utilizing Java, Python, and MySQL to architect solutions for critical business data management. Designed and implemented database reports and automation solutions to streamline banking operations.
                      </p>`,
        // If you have a logo for them, change this to iconImage: 'img/timeline/rave.png'
        iconImage: 'img/timeline/rave.png',
      },

      // --- TECHNICAL ROLES ---
      {
        detailed   : true, 
        transparent: true,
        year       : '2019 - 2021',
        title      : 'Technical Sales & Engineering - Scallon Controls',
        html       : 'Mastered the DeltaV system by Emerson. Worked directly with engineers to integrate hardware, servers, and controllers for the Oil & Gas industry.',
        iconImage  : 'img/timeline/scallon-controls.png',
      },
      {
        detailed   : true,
        transparent: true,
        year       : '2020 - 2022',
        title      : 'Adjunct Instructor - Lamar Institute of Technology',
        html       : 'Taught evening classes in Web Design, PHP, and Database Management. This role honed my ability to break down complex technical concepts for diverse audiences—a key skill in global support training.',
        iconImage  : 'img/timeline/LIT.jpg',
      },

      // --- THE LEADERSHIP ERA ---
      {
        detailed   : false,
        transparent: true,
        year       : '2021 - 2022',
        title      : 'Tier 2 Support Engineer - Identity Automation',
        html       : `<p>
                      <strong>The Technical Pivot.</strong><br>
                      Returned to pure engineering to modernize my skillset. Supported AWS-hosted environments, implemented SAML/OpenID authentication, 
                      and wrote Python scripts to automate customer fixes. This role proved I can still "do the work" while leading the team.
                      </p>`,
        iconImage  : 'img/timeline/identityautomation.png',
      },
      {
        detailed   : false,
        transparent: true, 
        year       : '2022 - 2025',
        title      : 'Senior Support Team Leader - Sycurio',
        html       : `<p>
                      <strong>Global Team Management.</strong><br>
                      Initially hired to lead the U.S. team, my role expanded to oversee Support Engineers across the U.S. and UK. 
                      I managed the digital roadmap, ensured PCI compliance, and introduced automation that reduced ticket resolution time for routine tasks.
                      </p>`,
        iconImage  : 'img/timeline/Sycurio.jpg',
      },
      {
        detailed   : false,
        transparent: true, 
        year       : 'FEB 2025 - DEC 2025',
        title      : 'Head of Customer Support - Sycurio',
        html       : `<p>
                      <strong>Strategic Leadership.</strong><br>
                      Promoted to lead the entire Global Support division. Reporting directly to the VP of Customer Delivery.<br>
                      <br>
                      <ul>
                        <li><strong>Strategy:</strong> Defining the support roadmap to align with business goals.</li>
                        <li><strong>People:</strong> Managing 10+ engineers across multiple time zones, focusing on career development and retention.</li>
                        <li><strong>Process:</strong> Implementing data-driven optimizations to improve CSAT and reduce TTM (Time to Mitigate).</li>
                      </ul>
                      </p>`,
        iconImage  : 'img/timeline/Sycurio.jpg',
      },
      {
        detailed   : false,
        transparent: false, // Made this one fully opaque since it is current
        year       : 'MAR 2026 - Present',
        title      : 'Customer Success Manager - IDEMIA Public Security',
        html       : `<p>
                      <strong>Client Integration & Support.</strong><br>
                      Acting as the primary touch point for onboarding and supporting client integration to AWS SaaS Services for Identity Verification and Digital ID.<br>
                      <br>
                      <ul>
                        <li><strong>Technical Troubleshooting:</strong> Resolving client issues related to product integration and performance, managing T1/T2 support, and tracking tickets via Jira and Salesforce.</li>
                        <li><strong>Platform Expertise:</strong> Serving as a Subject Matter Expert (SME) on IDEMIA SaaS Solutions, API, and SDK integrations.</li>
                        <li><strong>Infrastructure & Architecture:</strong> Implementing and maintaining client AWS tenants while advising the Pre-Sales Engineering team on Solution Architecture and design.</li>
                      </ul>
                      </p>`,
        iconImage  : 'img/timeline/idemia.png',
      },
    ],
  }),
  computed: {
    orderedItems () {
      const items = [...this.items].reverse()
      if (this.detailed)
        return items
      return items.filter((item) => {
        return !item.detailed
      })
    },
  },
}
</script>

<style scoped>
/* Title underline to match the red theme */
.title {
  border-bottom: 2px #d32f2f solid;
  line-height: 1.5 !important;
}

.pre {
  white-space: pre;
}

/* Timeline card transparency handling for past jobs */
.transparent {
  opacity: 0.75; 
}

/* --- THEME & BACKGROUND CSS --- */

/* 1. The Engineering Dot Pattern (SVG) */
.bg-dot-pattern {
  background-color: #ffffff !important;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='3' cy='3' r='1.5' fill='%231565c0' fill-opacity='0.15'/%3E%3C/svg%3E") !important;
  background-repeat: repeat !important; 
}

/* 2. FIX FOR BLOCKED DOTS: Force all inner layout wrappers to be transparent */
::v-deep .v-card__text > div {
  background-color: transparent !important;
}
::v-deep .theme--light.v-timeline {
  background-color: transparent !important;
}

/* 3. Ensure the actual timeline cards STAY solid white so text is readable */
::v-deep .timeline-card {
  background-color: #ffffff !important;
}

/* 4. Initial Page Load Animation */
.fade-in-up {
  animation: fadeInUp 1s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>